import React, {useState} from 'react'
import {observer} from 'mobx-react'
import {Box, Button, Flex, Stack, Text} from '../../../vanilla'
import {
    DeliveryCardStandardIcon,
    CalendarFold,
    ArrowRightIcon,
    FeatureCheckIcon,
    DeliveryCardRapidIcon
} from '../../../components/icons'
import {RapidDeliveryETA} from '../../../components/book-delivery/rapid-delivery-eta'
import useNavigation from '../../../hooks/use-navigation'
import {
    DELIVERY_CARD_CONTENT_NAMES,
    useDeliveryCardTracking
} from '../../../analytics/select-content-tracking'
import * as styles from './delivery-box.css'
import { DeliveryBoxProps, DELIVERY_INFO_SELECTORS } from '../../../types/delivery'

const DELIVERY_TYPE_PATHS: Record<string, string> = {
    'standard': '/book-delivery?type=nextDay',
    'sameday': '/book-delivery?type=sameDay',
    'rapid': '/book-delivery?type=rapid',
    'extended': '/pre-book-delivery'
}

const getDeliveryTypeSelector = (deliveryType?: string) =>
    deliveryType?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

const isHttpUrl = (value?: string | null) => {
    if (!value) return false

    try {
        const url = new URL(value)
        return url.protocol === 'http:' || url.protocol === 'https:'
    } catch {
        return false
    }
}

export const DeliveryBox = observer((props: DeliveryBoxProps) => {
    const [isFeaturesOpen, setIsFeaturesOpen] = useState(false)
    const navigate = useNavigation()
    const sendDeliveryCardSelectContent = useDeliveryCardTracking()
    const {
        deliveryType,
        title,
        backgroundImage,
        available = true,
        lowestCost,
        unavailableMessage,
        deliveryTypeSubtext,
        features,
        minSpend,
        loading = false,
        icon,
        ctaText,
        theme,
        contentClassName
    } = props

    const handleBookSlot = () => {
        if (!available) {
            return
        }

        const path = deliveryType ? DELIVERY_TYPE_PATHS[deliveryType] : undefined
        const contentKey = DELIVERY_INFO_SELECTORS[deliveryType || '']
        const contentName = contentKey
            ? DELIVERY_CARD_CONTENT_NAMES[contentKey as keyof typeof DELIVERY_CARD_CONTENT_NAMES]
            : undefined

        if (contentName && path) {
            sendDeliveryCardSelectContent({contentName, contentUrl: path})
        }

        if (path) {
            navigate(path, 'push')
        }
    }

    const deliveryIcons = {
        'delivery': DeliveryCardStandardIcon,
        'rapid': DeliveryCardRapidIcon,
        'calendar-fold': CalendarFold
    }

    const iconIsUrl = isHttpUrl(icon)
    const DeliveryIcon = !iconIsUrl && icon
        ? deliveryIcons[icon as keyof typeof deliveryIcons] || DeliveryCardStandardIcon
        : DeliveryCardStandardIcon
    const deliveryTypeSelector = getDeliveryTypeSelector(deliveryType)
    const deliveryInfoSelector = DELIVERY_INFO_SELECTORS[deliveryType || '']
    const featureContent = features.map((feature, index) => (
        <Flex key={index} gap="8px" color={theme === 'light' ? 'green' : 'white'}>
            <FeatureCheckIcon aria-hidden="true" flexShrink="0" marginTop="4px" />
            <Text variant="text1" color={theme === 'light' ? 'black' : 'white'}>{feature}</Text>
        </Flex>
    ))

    const mobileBookButton = (
        <Box display={['flex', 'none']} justifyContent="flex-end">
        <Button
            isLoading={loading}
            spinnerSize="sm"
            variant="primaryCircular"
            disabled={!available}
            aria-label={available ? 'Book a Slot' : 'Not available right now'}
            data-test-selector={`delivery-box-${deliveryTypeSelector}-book-slot-mobile`}
            padding="12px"
            onClick={handleBookSlot}
        >
            <ArrowRightIcon width="24px" height="24px" color="white" aria-hidden="true" />
        </Button>
        </Box>
    )

    return (
    <Box
        className={contentClassName}
        flex="1"
        width="full"
        height="full"
        data-test-selector={`delivery-box-${deliveryTypeSelector}`}
        display="flex"
        flexDirection="column"
        style={{
            color: theme === 'light' ? 'black' : 'white'
        }}
        >
        <Box
            marginBottom="16px"
            display="flex"
            flexDirection={['row', 'column']}
            alignItems={['center', 'stretch']}
            gap={['12px', '0px']}
            position="relative"
        >
            {iconIsUrl ? (
                <Box
                    as="img"
                    src={icon || undefined}
                    alt=""
                    aria-hidden="true"
                    marginBottom={['0px', '16px']}
                    boxSize="56px"
                    style={{objectFit: 'contain'}}
                />
            ) : (
                <DeliveryIcon marginBottom={['0px', '16px']} boxSize="56px" aria-hidden="true" />
            )}
            <Box flex="1">
            <Text variant="heading1SemiBold" data-test-selector={`delivery-box-${deliveryTypeSelector}-title`}>{title}</Text>
            <Text variant="text3">{deliveryTypeSubtext}</Text>
            </Box>
            {deliveryType === 'Rapid Delivery' && available && (
            <Text
                position="absolute"
                top="0px"
                right="0px"
                paddingX="6px"
                color="white"
                as="span"
                borderRadius="base"
                display="inline"
                variant="text1"
                backgroundColor="accent5"
            >
                Fastest
            </Text>
            )}
        </Box>

        {unavailableMessage ? (
            <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                gap="12px"
                marginBottom={['28px', '16px']}
                data-test-selector={`delivery-box-${deliveryTypeSelector}-unavailable`}
            >
                <Text variant="text7">{unavailableMessage}</Text>
            </Box>
        ) : (
            <>
                <Box display={['block', 'none']}>
                    <Button
                        variant="unstyled"
                        paddingX="0px"
                        style={{fontWeight: 300}}
                        color={backgroundImage ? 'white' :"accent5"}
                        textDecoration="none"
                        justifyContent="flex-start"
                        onClick={() => setIsFeaturesOpen((isOpen) => !isOpen)}
                        aria-expanded={isFeaturesOpen}
                        data-test-selector={`delivery-box-${deliveryTypeSelector}-more-info`}
                    >
                        {isFeaturesOpen ? 'Less Info -' : 'More Info +'}
                    </Button>
                </Box>

                {isFeaturesOpen && (
                    <Box
                        display={['block', 'none']}
                        backgroundColor={theme === 'light' ? 'gray75' : undefined}
                        color={ theme === 'light' ? 'text0' : 'white' }
                        borderRadius="base"
                        padding="8px"
                        data-test-selector={`delivery-info-${deliveryInfoSelector}-delivery`}
                    >
                        {featureContent}
                    </Box>
                )}
                <Stack
                    display={['none', 'flex']}
                    spacing="0px"
                    data-test-selector={`delivery-info-${deliveryInfoSelector}-delivery`}
                >
                    {featureContent}
                </Stack>

                <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-between"
                    alignItems="center"
                    marginY={[available ? '0px' : '16px', '16px']}
                >
                    <Stack
                        spacing="0px"
                        data-test-selector={`delivery-fee-${deliveryInfoSelector}-delivery`}
                    >
                    {lowestCost !== null && lowestCost !== undefined && (
                        <Text variant="unstyled" size="xs" weight="semibold">
                        {lowestCost === 0 ? 'From FREE' : `From £${lowestCost.toFixed(2)}`}
                        </Text>
                    )}
                    {deliveryType === 'Rapid Delivery' ? (
                        <Box className={styles.rapidDeliveryEta}>
                            <RapidDeliveryETA variant="bookDelivery" />
                        </Box>
                    ) : (
                        minSpend && deliveryType !== 'Pre-Book Delivery' ? (
                        <Text variant="unstyled" size="xs" weight="normal">
                            Min spend £{minSpend}.
                        </Text>
                        ) : null
                    )}
                    </Stack>
                    {available && mobileBookButton}
                </Box>
            </>
        )}

        <Stack marginTop="auto">
            <Box display={[available ? 'none' : 'block', 'block']} width="full">
            <Button
                isLoading={loading}
                width="full"
                variant={available ? 'primary' : 'neutral'}
                disabled={!available}
                onClick={handleBookSlot}
                data-test-selector={`delivery-box-${deliveryTypeSelector}-book-slot-desktop`}
                style={available ? undefined : {opacity: 1}}
            >
                {available ? ctaText : 'Not available right now'}
            </Button>
            </Box>
        </Stack>
        </Box>
    )
})
